import {
  Box,
  CircularProgress,
  Flex,
  Heading,
  Image,
  Text,
  useBreakpointValue,
  useDisclosure
} from "@chakra-ui/react"
import { FC, useContext, useEffect, useMemo, useState } from "react"
import { toast } from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { MdDelete, MdOutlineClear } from "react-icons/md"
import { useHistory } from "react-router-dom"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { SDKContext } from "src/context/SDKContext"
import { AuthPageStyling, PlansPageStyling } from "src/context/types"
import useAuthedUser from "src/hooks/useAuthedUser"
import { useChatGenerationValues } from "src/hooks/useChatGenerationValues"
import useConfig from "src/hooks/useConfig"
import useOnClickWithTouchHandler from "src/hooks/useOnClickWithTouchHandler"
import usePageStyling from "src/hooks/usePageStyling"
import useSearchParams from "src/hooks/useSearchParams"
import { useValidateRoom } from "src/hooks/useValidateRoom"
import {
  AvatarDimensionality,
  avatarsSelector,
  gridSelectedAvatarAtom,
  gridSelectedAvatarDimensionalityAtom,
  navigatedFromAvatarsListAtom
} from "src/recoil/atoms/avatars"
import { AvatarConfig, AvatarDetail } from "src/recoil/atoms/avatars/types"
import { currentChatGenerationAtom } from "src/recoil/atoms/chats"
import axios from "src/shared/axios"
import { addCurrentQueryParams } from "src/util"
import { useIsMobileDevice } from "src/util/browser"
import { useIsMobileApp } from "src/util/deviceType"
import {
  PopupModal,
  PopupModalButton
} from "../../account/components/PopupModal"
import { PlansButton } from "../../plans/components/PlansButton"
import PerspectiveBtn from "../components/perspective-btn"
import { PerspectiveBtnAlt } from "../components/PerspectiveBtnAlt"
import useGridPageStyling from "../hooks/useGridPageStyling"
import { RecentChatModel, useRecentChats } from "../hooks/useRecentChats"

export const RecentChats: FC = () => {
  const { getChatGenerationValues, putChatGenerationValue } =
    useChatGenerationValues()
  const chatGenerationValues = getChatGenerationValues()

  const { chats, invalidateRecentChats } = useRecentChats()
  const [selectedIdx, setSelectedIdx] = useState(null)
  const { avatars } = useRecoilValue(avatarsSelector) || ({} as AvatarDetail)
  const chatsWithAvatars = chats
    ?.map(chat => {
      const cgv = chatGenerationValues?.find(
        c => c.customerAvatarId.toString() == chat.toAvatarId
      )
      const avatar = avatars?.find(avatar => {
        return avatar?.id?.toString() === chat.toAvatarId
        // return (
        //   avatar.avatarId == chat.toAvatarId ||
        //   avatar.avatarId3d == chat.toAvatarId
        // )
      })
      return { ...chat, avatar, cgv }
    })
    .filter(c => c.generation === (c.cgv?.generation ?? 0)) // make sure its the latest one

  // filter out avatar duplicates
  const avatarIdsEncountered = []
  const finalChats = chatsWithAvatars?.filter(chat => {
    if (chat.avatar == null) return false
    if (
      avatarIdsEncountered.includes(chat.avatar.avatarId) ||
      avatarIdsEncountered.includes(chat.avatar.avatarId3d)
    ) {
      return false
    } else {
      if (chat.avatar.avatarId) avatarIdsEncountered.push(chat.avatar.avatarId)
      if (chat.avatar.avatarId3d)
        avatarIdsEncountered.push(chat.avatar.avatarId3d)
      return true
    }
  })
  const styles: AuthPageStyling = usePageStyling("authPageStyling")
  const branding = useGridPageStyling()

  const setGridSelectedAvatar = useSetRecoilState(gridSelectedAvatarAtom)
  const setGridSelectedAvatarDimensionality = useSetRecoilState(
    gridSelectedAvatarDimensionalityAtom
  )
  const setNavigatedFromAvatarsList = useSetRecoilState(
    navigatedFromAvatarsListAtom
  )

  const { authedUser, getAuthedUser } = useAuthedUser()
  const meetingClient = useContext(SDKContext)
  const { validateRoom } = useValidateRoom(meetingClient)
  const isMobileApp = useIsMobileApp()
  const config = useConfig()
  const history = useHistory()
  const params = useSearchParams<{ go_back: "journey" }>()
  const returnRoute = params.go_back
  const navigatedFromJourney = returnRoute === "journey"
  let featureEnabled = config.avatarsGrid || navigatedFromJourney

  useEffect(() => {
    getAuthedUser()
  }, [])

  useEffect(() => {
    if (meetingClient && featureEnabled) {
      console.log("Connecting avatars")
      meetingClient?.connectAvatars(axios)
    }
  }, [meetingClient])

  const handleOpenRoom = async (
    dimensionality: AvatarDimensionality,
    recentChat: RecentChatModel & { avatar: AvatarConfig }
  ) => {
    setGridSelectedAvatarDimensionality(dimensionality)
    localStorage.setItem("avatarConfig", JSON.stringify(recentChat.avatar))
    setGridSelectedAvatar(recentChat.avatar)
    // Disconnect the previous meeting if active
    // await meetingClient?.disconnectGateway(0)

    meetingClient.setRoomHash(recentChat.roomHash)
    const joinRoom = !!authedUser?.screenName
    if (joinRoom) meetingClient?.setUserName(authedUser?.screenName)
    await validateRoom({ joinRoomDirectly: joinRoom })

    if (isMobileApp) {
      history.replace(addCurrentQueryParams(recentChat.roomHash))
    } else {
      history.push(addCurrentQueryParams(recentChat.roomHash))
    }

    // If we are not join room direct then navigate to home page, So that user can set userName
    // if (!joinRoom) {
    //   navigateToRoomHash(roomHash)
    //   setView(Views.HOME)
    // } else {
    //   navigateToRoomHash(roomHash)
    //   setView(Views.MEETING_ROOM)
    // }

    // set true so that on leave we can redirect user to show screen
    setNavigatedFromAvatarsList(true)
  }

  const [avatarToClearChatsFor, setAvatarToClearChatsFor] = useState<{
    name: string
    customerAvatarId: number
  }>(null)

  const containerWidth = useBreakpointValue({ base: "100%", md: "70%" })
  const { isOpen, onOpen, onClose } = useDisclosure()
  const plansPageStyles: PlansPageStyling = usePageStyling("plansPageStyling")
  const { t } = useTranslation()

  return (
    <Flex
      h="100%"
      paddingTop={"19px"}
      align={!finalChats || finalChats?.length == 0 ? "center" : "start"}
      justify="center"
    >
      <PopupModal
        isOpen={isOpen}
        branding={plansPageStyles}
        onClose={onClose}
        title={t("Are you sure?")}
        description={
          t("This will clear your chat history and avatar memory for ") +
          avatarToClearChatsFor?.name +
          "."
        }
        action={
          <PopupModalButton
            onClick={async () => {
              // check if the cgv already exists, if not we'll create one without an id (put will upsert)
              const authedUser = await getAuthedUser()

              const cgv = chatGenerationValues?.find(
                c =>
                  avatarToClearChatsFor.customerAvatarId === c.customerAvatarId
              ) ?? {
                customerAvatarId: avatarToClearChatsFor.customerAvatarId,
                generation: 0,
                ownerId: authedUser.userId
              }

              const promise = putChatGenerationValue({
                ...cgv,
                generation: cgv.generation + 1
              }).then(invalidateRecentChats)
              onClose()
              toast.promise(promise, {
                error: "Something went wrong",
                loading: "Loading...",
                success: `Chat with ${avatarToClearChatsFor.name} cleared`
              })
            }}
          >
            {t("Clear memory")}
          </PopupModalButton>
        }
      />
      {!finalChats || finalChats?.length == 0 ? (
        finalChats?.length === 0 ? (
          <Flex direction="column" gap="5px">
            <Heading color="white" textAlign={"center"}>
              No recent chats.
            </Heading>
            <Heading color="white" textAlign={"center"}>
              Start a new chat in the Explore tab.
            </Heading>
          </Flex>
        ) : (
          <CircularProgress color={branding.panelTextColor} isIndeterminate />
        )
      ) : (
        <Flex
          direction={"column"}
          width="100%"
          alignItems={"center"}
          gap="10px"
          mt="5px"
        >
          <Flex width={containerWidth} flexDirection={"column"} gap="15px">
            {finalChats?.map((chat, index) => (
              <RecentChat
                key={index}
                idx={index}
                chat={chat}
                clearAvatarChat={() => {
                  onOpen()
                  setAvatarToClearChatsFor({
                    customerAvatarId: chat.avatar.id,
                    name: chat.avatar.avatarName
                  })
                }}
                handleOpenRoom={handleOpenRoom}
                isSelected={selectedIdx === index}
                onSelected={setSelectedIdx}
              />
            ))}
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}

const RecentChat = ({
  idx,
  chat,
  isSelected,
  onSelected,
  handleOpenRoom,
  clearAvatarChat
}) => {
  const miniCard = false
  const isMobile = useIsMobileDevice()
  const branding = useGridPageStyling()

  const cardStyle = useMemo(() => {
    if (miniCard) {
      return {
        cardHeight: "unset",
        cardWidth: isMobile ? "86%" : "280px",
        thumbnailWidth: "50px",
        thumbnailHeight: "50px",
        avatarHeight: "30px",
        avatarWidth: "30px",
        descriptionNoOfLine: 2
      }
    }
    return {
      cardHeight: "unset",
      cardWidth: "100%",
      thumbnailWidth: "50px",
      thumbnailHeight: "50px",
      avatarHeight: "32px",
      avatarWidth: "32px",
      descriptionNoOfLine: 2
    }
  }, [miniCard])

  return (
    <Box
      mx="2px"
      position="relative"
      onTouchStart={() => {
        if (isMobile) onSelected(idx)
      }}
      minWidth={cardStyle.cardWidth}
      maxWidth={cardStyle.avatarWidth}
      height={cardStyle.cardHeight}
      minHeight={cardStyle.cardHeight}
      p={3}
      bg={branding.panelBackgroundColor}
      // bg={'rgba(34, 34, 34, 0.9)'}
      color={branding.panelTextColor}
      borderColor={
        isSelected
          ? branding.panelMouseoverBorderColor
          : branding.panelBorderColor
      }
      borderWidth={2}
      _hover={
        !isMobile
          ? {
              borderColor: branding.panelMouseoverBorderColor
            }
          : {}
      }
      borderRadius={"6px"}
      shadow="md"
      style={{ scrollSnapAlign: "center" }}
      cursor="pointer"
      role="group" // added group for applying _groupHover to child
    >
      <Flex
        height={"full"}
        width={"full"}
        direction={"row"}
        alignItems="center"
      >
        <Box pr={1} position={"relative"} display="flex" alignItems="center">
          <Image
            borderRadius={"full"}
            objectFit="cover"
            minWidth={cardStyle.thumbnailWidth}
            width={cardStyle.thumbnailWidth}
            height={cardStyle.thumbnailHeight}
            src={chat.avatar.bubbleImage}
          />
        </Box>
        <Flex height={"full"} px={1} direction={"column"} overflow="hidden">
          <Text
            fontWeight={"semibold"}
            whiteSpace="nowrap"
            textOverflow={"ellipsis"}
            overflow={"hidden"}
            fontSize={{ base: "18px", md: "24px" }}
          >
            {chat.avatar.avatarName}
          </Text>
          <Text
            whiteSpace="normal"
            noOfLines={cardStyle.descriptionNoOfLine}
            fontSize={{ base: "12px", md: "16px" }}
          >
            {chat.message}
          </Text>
        </Flex>

        <Flex marginLeft={"auto"}>
          <Flex
            marginLeft={"auto"}
            flexDirection={"column"}
            alignItems="end"
            maxHeight={"60px"}
            // width="100px"
            justifyContent="center"
            gap="5px"
            visibility={isMobile && isSelected ? "visible" : "hidden"}
            _groupHover={
              !isMobile && {
                visibility: "visible"
              }
            }
          >
            {!!chat.avatar.avatarId3d && (
              <Flex
                // marginTop={"2px"}
                // width="20px"
                // height="20px"
                // width={"110px"}
                // marginX="-20px"
                justifyContent="center"
                alignItems={"center"}
                color={branding.panelMouseoverBackgroundColor}
              >
                <PerspectiveBtnAlt
                  height="30px"
                  width="60px"
                  type="3D"
                  onClick={() =>
                    handleOpenRoom(AvatarDimensionality.threeDimensional, chat)
                  }
                  border={`2px solid currentColor`}
                  iconSize="80%"
                  borderRadius="20px"
                />
              </Flex>
            )}
            {!!chat.avatar.avatarId && (
              <Flex
                // width={"110px"}
                // marginX="-20px"
                justifyContent="center"
                alignItems={"center"}
                color={branding.panelMouseoverBackgroundColor}
              >
                <PerspectiveBtnAlt
                  height="30px"
                  width="60px"
                  type="2D"
                  onClick={() =>
                    handleOpenRoom(AvatarDimensionality.twoDimensional, chat)
                  }
                  border={`2px solid currentColor`}
                  iconSize="70%"
                  borderRadius="20px"
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>

      <Flex
        position={"absolute"}
        top="-10px"
        left={"-10px"}
        // maxHeight={"60px"}
        // width="100px"
        // gap="5px"
        visibility={isMobile && isSelected ? "visible" : "hidden"}
        _groupHover={
          !isMobile && {
            visibility: "visible"
          }
        }
      >
        <Button
          borderRadius="9999px"
          width="20px"
          height="20px"
          onClick={clearAvatarChat}
          border={`2px solid ${branding.panelMouseoverBorderColor}`}
        >
          <MdOutlineClear color="white" />
        </Button>
      </Flex>
    </Box>
  )
}

type Props = {
  onClick: () => unknown
  borderRadius: string
  height?: string
  adjustMobileTapBehaviour?: boolean
  width?: string
}

const Button: FC<Props & React.CSSProperties> = ({
  children,
  onClick,
  height,
  width,
  borderRadius,
  adjustMobileTapBehaviour = true,
  ...props
}) => {
  const isMobile = useIsMobileDevice()
  const _onClick = adjustMobileTapBehaviour
    ? useOnClickWithTouchHandler(onClick)
    : null

  return (
    <Flex
      alignItems={"center"}
      justifyContent="center"
      onClick={onClick}
      {..._onClick}
      // onClick={onClick}
      // onClick={isMobile ? null : onClick}
      // onTouchStart={isMobile ? onClick : null}
      height={height}
      width={width}
      style={{
        aspectRatio: "4/2"
      }}
      bg="#212121"
      border={props.border ?? "4px solid currentColor"}
      borderRadius={borderRadius}
      outline="none"
      transition="background .25s ease"
      _hover={
        !isMobile && {
          bg: "black"
        }
      }
      _active={{}}
      _focus={{}}
      padding={props.padding}
    >
      {children}
    </Flex>
  )
}
